import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Expenses.css";

const API = "https://okna.co.tz/mrms/Expenses.php";
const ITEMS_PER_PAGE = 10;

const CATEGORIES = [
    "BOTTLES", "CONSULTING", "DECORATION", "DELIVERY FEE", "IT AND INTERNET",
    "MOBILE MNG", "OFFICE: STATIONARY", "PACKAGING MATERIALS", "RAW", "TRANSPORT",
    "CLEANING", "COFFEE MACHINE RENT", "STAFF RECURATIONS", "SALES & MARKETING",
    "BURGER PAYMENT", "SERVICE & REPAIR", "KITCHENWARE", "ELECTRICITY", "GROCERIES",
    "SALARY", "ADVANCE PAYMENT", "SALES COMMISSION", "TRANSFERS; OKNA BANK",
    "DIRECTORS", "NETWORK COMMISSION", "HARDWARE"
];

const PAYMENT_METHODS = ["CASH", "CRDB_BANK", "SELECOM", "M-PESA", "MIXX_BY_YAS"];
const RECEIPT_OPTIONS = ["YES", "NO"];

const REPORT_TYPES = [
    { value: "last_60_minutes", label: "⏱️ Last 60 Minutes", icon: "⏱️", color: "#f97316" },
    { value: "last_24_hours", label: "🕐 Last 24 Hours", icon: "🕐", color: "#3b82f6" },
    { value: "today", label: "🌞 Today", icon: "🌞", color: "#eab308" },
    { value: "yesterday", label: "🌙 Yesterday", icon: "🌙", color: "#6b7280" },
    { value: "last_7_days", label: "📅 Last 7 Days", icon: "📅", color: "#10b981" },
    { value: "this_week", label: "📋 This Week", icon: "📋", color: "#8b5cf6" },
    { value: "last_week", label: "📉 Last Week", icon: "📉", color: "#ef4444" },
    { value: "last_30_days", label: "📊 Last 30 Days", icon: "📊", color: "#14b8a6" },
    { value: "this_month", label: "📈 This Month", icon: "📈", color: "#f97316" },
    { value: "last_month", label: "📉 Last Month", icon: "📉", color: "#8b5cf6" },
    { value: "day_to_day", label: "📅 Custom Range", icon: "📅", color: "#ec4899" }
];

export default function Expenses() {
    const token = localStorage.getItem("token");
    const [expenses, setExpenses] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [reportType, setReportType] = useState("today");
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [isExporting, setIsExporting] = useState(false);
    const [form, setForm] = useState({
        category: "", payment_method: "CASH", amount: "", description: "", receipt: "NO"
    });
    const [selectedExpense, setSelectedExpense] = useState(null);

    useEffect(() => {
        if (!token) {
            Swal.fire("Session expired", "Please login again", "warning");
            return;
        }
        loadExpenses();
    }, [token]);

    const loadExpenses = async () => {
        try {
            // Add token to URL
            const res = await fetch(`${API}?token=${token}`, {
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }
            
            const data = await res.json();
            if (Array.isArray(data)) {
                setExpenses(data);
            } else if (data.error) {
                Swal.fire("Error", data.error, "error");
            }
        } catch (error) {
            console.error("Load error:", error);
            Swal.fire("Error", "Server not reachable", "error");
        }
    };

    const handleCategorySearch = (input) => {
        if (input.length > 0) {
            const matches = CATEGORIES.filter(cat => 
                cat.toLowerCase().includes(input.toLowerCase())
            );
            setFilteredCategories(matches);
            setShowCategoryDropdown(matches.length > 0);
        } else {
            setShowCategoryDropdown(false);
        }
    };

    const selectCategory = (category) => {
        setForm(prev => ({ ...prev, category }));
        setShowCategoryDropdown(false);
    };

    const exportExpenses = async () => {
        setIsExporting(true);
        
        try {
            // Add token to URL
            let url = `${API}?export_expenses=1&token=${token}&report=${reportType}`;
            
            if (reportType === "day_to_day") {
                url += `&start=${startDate}&end=${endDate}`;
            }
            
            const response = await fetch(url, {
                headers: { 
                    "Authorization": `Bearer ${token}`
                }
            });
            
            if (response.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Export failed");
            }
            
            // Get filename from Content-Disposition header or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = "expenses_report.xlsx";
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }
            
            // Download the file
            const blob = await response.blob();
            const url_blob = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url_blob;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url_blob);
            
            Swal.fire({
                title: "✅ Export Successful!",
                text: "Excel report has been downloaded successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            
            setShowReportModal(false);
            
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                title: "Export Failed",
                text: error.message || "Failed to generate report. Please try again.",
                icon: "error",
                confirmButtonColor: "#d33"
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (name === "category") handleCategorySearch(value);
    };

    const resetForm = () => {
        setForm({ category: "", payment_method: "CASH", amount: "", description: "", receipt: "NO" });
        setIsEditing(false);
        setSelectedExpense(null);
        setShowCategoryDropdown(false);
    };

    const openAddForm = () => { resetForm(); setShowFormModal(true); };

    const openEditForm = expense => {
        setForm({
            category: expense.category,
            payment_method: expense.payment_method,
            amount: expense.amount,
            description: expense.description || "",
            receipt: expense.receipt || "NO"
        });
        setSelectedExpense(expense);
        setIsEditing(true);
        setShowFormModal(true);
    };

    const saveExpense = async () => {
        if (!form.category) return Swal.fire("Error", "Category is required", "warning");
        if (!form.amount || parseFloat(form.amount) <= 0) return Swal.fire("Error", "Valid amount is required", "warning");

        try {
            const payload = {
                category: form.category,
                payment_method: form.payment_method,
                amount: parseFloat(form.amount),
                description: form.description || null,
                receipt: form.receipt
            };

            const url = isEditing ? `${API}?id=${selectedExpense.id}&token=${token}` : `${API}?token=${token}`;
            const method = isEditing ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                headers: { 
                    "Content-Type": "application/json", 
                    "Authorization": `Bearer ${token}` 
                },
                body: JSON.stringify(payload)
            });
            
            // Check for 401 Unauthorized
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }

            const data = await res.json();
            if (data.error) return Swal.fire("Error", data.error, "error");
            
            Swal.fire("Success", "Saved successfully", "success");
            setShowFormModal(false);
            resetForm();
            loadExpenses();
        } catch (error) {
            console.error("Save error:", error);
            Swal.fire("Error", "Failed to save expense", "error");
        }
    };

    const deleteExpense = async expense => {
        const confirm = await Swal.fire({
            title: "Delete Expense?",
            text: `${expense.category} - ${expense.amount} TZS`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33"
        });
        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(`${API}?id=${expense.id}&token=${token}`, {
                method: "DELETE",
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }
            
            const data = await res.json();
            if (data.error) {
                Swal.fire("Error", data.error, "error");
                return;
            }
            
            setExpenses(prev => prev.filter(e => e.id !== expense.id));
            Swal.fire("Deleted", "Expense removed", "success");
        } catch (error) {
            console.error("Delete error:", error);
            Swal.fire("Error", "Delete failed", "error");
        }
    };

    const formatAmount = amount => amount.toLocaleString();
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated = expenses.slice(start, start + ITEMS_PER_PAGE);
    const totalPages = Math.ceil(expenses.length / ITEMS_PER_PAGE);

    return (
        <div className="ex-container">
            <div className="ex-header">
                <div className="ex-header-left">
                    <h2><span className="ex-header-icon">💰</span> Expenses Management</h2>
                    <p className="ex-header-subtitle">Track & manage all business expenses</p>
                </div>
                <div className="ex-header-actions">
                    <button onClick={() => setShowReportModal(true)} className="ex-btn-report">
                        <span>📊</span> Excel Report
                    </button>
                    <button onClick={openAddForm} className="ex-btn-primary">
                        <span>➕</span> Add Expense
                    </button>
                </div>
            </div>

            <div className="ex-stats-grid">
                <div className="ex-stat-card total">
                    <div className="ex-stat-icon">📋</div>
                    <div className="ex-stat-content">
                        <span className="ex-stat-label">Total Expenses</span>
                        <span className="ex-stat-value">{expenses.length}</span>
                    </div>
                </div>
                <div className="ex-stat-card amount">
                    <div className="ex-stat-icon">💵</div>
                    <div className="ex-stat-content">
                        <span className="ex-stat-label">Total Amount</span>
                        <span className="ex-stat-value">{expenses.reduce((s,e) => s + e.amount, 0).toLocaleString()} TZS</span>
                    </div>
                </div>
                <div className="ex-stat-card average">
                    <div className="ex-stat-icon">📊</div>
                    <div className="ex-stat-content">
                        <span className="ex-stat-label">Average</span>
                        <span className="ex-stat-value">
                            {expenses.length > 0 ? (expenses.reduce((s,e) => s + e.amount, 0) / expenses.length).toLocaleString() : 0} TZS
                        </span>
                    </div>
                </div>
            </div>

            <div className="ex-table-wrapper">
                <table className="ex-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Category</th>
                            <th>Payment Method</th>
                            <th>Amount (TZS)</th>
                            <th>Receipt</th>
                            <th>Recorded By</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.map(e => (
                            <tr key={e.id}>
                                <td><span className="ex-date">{new Date(e.created_at).toLocaleDateString()}</span></td>
                                <td><span className={`ex-category-tag ex-cat-${e.category.toLowerCase().replace(/[&\s;]/g, '-')}`}>{e.category}</span></td>
                                <td><span className={`ex-payment-tag ex-pay-${e.payment_method.toLowerCase()}`}>{e.payment_method}</span></td>
                                <td className="ex-amount-cell">{formatAmount(e.amount)} TZS</td>
                                <td><span className={`ex-receipt-badge ${e.receipt === 'YES' ? 'yes' : 'no'}`}>{e.receipt}</span></td>
                                <td><span className="ex-staff-badge">{e.staff_name || 'System'}</span></td>
                                <td>
                                    <button onClick={() => openEditForm(e)} className="ex-action-btn edit" title="Edit">✏️</button>
                                    <button onClick={() => deleteExpense(e)} className="ex-action-btn delete" title="Delete">🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="ex-pagination">
                    <button disabled={currentPage===1} onClick={() => setCurrentPage(p=>p-1)} className="ex-page-btn prev">← Prev</button>
                    <span className="ex-page-info">{currentPage} / {totalPages}</span>
                    <button disabled={currentPage===totalPages} onClick={() => setCurrentPage(p=>p+1)} className="ex-page-btn next">Next →</button>
                </div>
            )}

            {showFormModal && (
                <div className="ex-modal" onClick={() => setShowFormModal(false)}>
                    <div className="ex-modal-content form-modal" onClick={e => e.stopPropagation()}>
                        <div className="ex-modal-header">
                            <h3>{isEditing ? "✏️ Edit Expense" : "➕ Add New Expense"}</h3>
                            <button onClick={() => setShowFormModal(false)} className="ex-modal-close">×</button>
                        </div>
                        
                        <div className="ex-form">
                            <div className="ex-form-group">
                                <label>Category <span className="required">*</span></label>
                                <div className="ex-search-wrapper">
                                    <input
                                        name="category"
                                        value={form.category}
                                        onChange={handleChange}
                                        placeholder="Type to search categories..."
                                        autoComplete="off"
                                    />
                                    {showCategoryDropdown && (
                                        <div className="ex-dropdown">
                                            {filteredCategories.map(c => (
                                                <div key={c} onClick={() => selectCategory(c)} className="ex-dropdown-item">
                                                    {c}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="ex-form-row">
                                <div className="ex-form-group">
                                    <label>Payment Method</label>
                                    <select name="payment_method" value={form.payment_method} onChange={handleChange}>
                                        {PAYMENT_METHODS.map(p => <option key={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="ex-form-group">
                                    <label>Amount <span className="required">*</span></label>
                                    <div className="ex-amount-input">
                                        <span className="ex-currency">TZS</span>
                                        <input name="amount" type="number" step="0.01" value={form.amount} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>

                            <div className="ex-form-row">
                                <div className="ex-form-group">
                                    <label>Receipt</label>
                                    <select name="receipt" value={form.receipt} onChange={handleChange}>
                                        {RECEIPT_OPTIONS.map(r => <option key={r}>{r}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="ex-form-group">
                                <label>Description</label>
                                <textarea name="description" rows="2" value={form.description} onChange={handleChange} placeholder="Enter description (optional)" />
                            </div>

                            <div className="ex-form-actions">
                                <button onClick={() => setShowFormModal(false)} className="ex-btn-cancel">Cancel</button>
                                <button onClick={saveExpense} className="ex-btn-save">{isEditing ? 'Update' : 'Save'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showReportModal && (
                <div className="ex-modal" onClick={() => setShowReportModal(false)}>
                    <div className="ex-modal-content report-modal" onClick={e => e.stopPropagation()}>
                        <div className="ex-modal-header">
                            <h3>📊 Generate Excel Report</h3>
                            <button onClick={() => setShowReportModal(false)} className="ex-modal-close">×</button>
                        </div>
                        
                        <div className="ex-report-filters">
                            <div className="ex-form-group">
                                <label>Report Period</label>
                                <select 
                                    value={reportType} 
                                    onChange={(e) => setReportType(e.target.value)} 
                                    className="ex-report-select"
                                >
                                    {REPORT_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>

                            {reportType === "day_to_day" && (
                                <div className="ex-form-row">
                                    <div className="ex-form-group">
                                        <label>From Date</label>
                                        <input 
                                            type="date" 
                                            value={startDate} 
                                            onChange={(e) => setStartDate(e.target.value)} 
                                        />
                                    </div>
                                    <div className="ex-form-group">
                                        <label>To Date</label>
                                        <input 
                                            type="date" 
                                            value={endDate} 
                                            onChange={(e) => setEndDate(e.target.value)} 
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="ex-form-actions">
                                <button 
                                    onClick={() => setShowReportModal(false)} 
                                    className="ex-btn-cancel"
                                    disabled={isExporting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={exportExpenses} 
                                    className="ex-btn-generate"
                                    disabled={isExporting}
                                >
                                    {isExporting ? '⏳ Generating...' : '📥 Generate Excel Report'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}